import { sourceInputAllowed } from '@/simulation/native/authority';
import { pixelControl } from '@/utils/tilePixels';
import { colors } from '@/styles';
import { Signal } from '@/models/data/Signal';
import { TileType } from '@/models/TileTypes';
import {
  defaultTileGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import {
  TileDefinition,
  CreateDefaultStateFn,
  TileGraphicsDefinition,
  CreateActionsFn,
} from '@/models/tiles/TileDefinition';
import { Side, TileSideIOType } from '@/schemas/tiles';
import { standardBuildAndConnect } from '@/models/logic/utils';
import { LogicNodeInstance } from '../logic/LogicNodeInstance';
import { LogicGraph } from '../logic/LogicGraph';
import { TileInstance } from './TileInstance';
import { ButtonNodeState } from '../logic/nodes/ButtonNode';
import { distinct } from '@/utils/object';

export interface ButtonTileState {
  toggleState: Signal;
}

export class ButtonTile implements TileDefinition<ButtonTileState> {
  type = TileType.Button;

  name = 'Button';
  canRotate = false;

  graphics = buttonTileGraphicsDefinition;

  createDefaultState: CreateDefaultStateFn<ButtonTileState> = () => ({
    toggleState: Signal.LOW,
  });

  buildAndConnectNodes = (data: any) => standardBuildAndConnect('BUTTON', data, this);

  createActions: CreateActionsFn<ButtonTileState> = ({ instance, eventBus }) => ({
    press() {
      if (!sourceInputAllowed(instance.context)) return;
      instance.state.toggleState = Signal.HIGH;
      if (!instance.logicNodeIds) return;
      const nodeIds = distinct(Object.values(instance.logicNodeIds));
      for (const nodeId of nodeIds) {
        eventBus?.trigger(nodeId, 'Button Pressed');
      }
    },
    depress() {
      if (!sourceInputAllowed(instance.context, true)) return;
      instance.state.toggleState = Signal.LOW;
      if (!instance.logicNodeIds) return;
      const nodeIds = distinct(Object.values(instance.logicNodeIds));
      for (const nodeId of nodeIds) {
        eventBus?.trigger(nodeId, 'Button Released');
      }
    },
  });

  syncStateToNodes = (data: {
    instance: TileInstance<ButtonTileState>;
    graph: LogicGraph;
  }) => {
    const { instance, graph } = data;

    if (!instance.logicNodeIds) return;

    const nodeIds = distinct(Object.values(instance.logicNodeIds));
    for (const nodeId of nodeIds) {
      const node = graph.nodes.get(nodeId) as LogicNodeInstance<ButtonNodeState>;

      if (!node) continue;

      node.state.isOn = instance.state.toggleState === Signal.HIGH;
    }
  };

  ioSides = {
    [Side.TOP]: { type: TileSideIOType.OUTPUT, forceConnection: false },
    [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: false },
    [Side.BOTTOM]: { type: TileSideIOType.OUTPUT, forceConnection: false },
    [Side.LEFT]: { type: TileSideIOType.OUTPUT, forceConnection: false },
  };
}

export const buttonTileGraphicsDefinition: TileGraphicsDefinition<ButtonTileState> = {
  type: TileType.Button,
  draw: function (g, { state, meta, connections, context }): void {
    defaultTileGraphics(g);

    const ioSides = {
      [Side.TOP]: { type: TileSideIOType.OUTPUT, forceConnection: false },
      [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: false },
      [Side.BOTTOM]: { type: TileSideIOType.OUTPUT, forceConnection: false },
      [Side.LEFT]: { type: TileSideIOType.OUTPUT, forceConnection: false },
    };

    connectionGraphics(
      g,
      colors.signalColor,
      colors.noSignalColor,
      meta,
      connections,
      ioSides,
      context,
    );

    const high = state.toggleState === Signal.HIGH;
    pixelControl(g, high ? colors.signalColor : colors.noSignalColor, true, high);
  },
};
